"use client";

import { IconTile } from "@cmp-src/data-display/IconTile";
import { useIsMobile } from "@cmp-src/hooks/use-mobile";
import { Button } from "@cmp-src/primitives/button";
import type {
  IResource,
  ISchedule,
  WorkplaceRequestElement
} from "@gettactic/api";
import { canScheduleOthers } from "@gettactic/helpers/src/permissions";
import { capitalize } from "@gettactic/helpers/src/strings/capitalize";
import { useAuthenticated } from "@gettactic/hooks";
import { format } from "date-fns";
import { ChevronRight, Plus } from "lucide-react";
import { useState } from "react";
import { useWorkplaceRequestModal } from "@/features/dashboard/contexts/WorkplaceRequestModalContext";
import { RequestDetailsPage } from "@/features/settings/components/WorkplaceRequestsPage/components/RequestDetailsPage";
import { useInvalidateTimelineEvents } from "@/features/timeline/hooks/useInvalidateTimelineEvents";
import {
  getRequestStatusColor,
  RequestStatusPill
} from "@/features/workplace-requests/components/RequestStatusPill";
import { useInvalidateWorkplaceRequestsByReservation } from "@/features/workplace-requests/hooks/useInvalidateWorkplaceRequestsByReservation";
import { useWorkplaceRequestTypeVisuals } from "@/features/workplace-requests/hooks/useWorkplaceRequestTypeVisuals";
import { NewRequestWizard } from "@/features/workplace-requests/views/queue/NewRequestWizard";
import { convertUTCToLocalDate } from "@/lib/utils";

const formatFiledOn = (created: string): string => {
  const date = new Date(created);
  return Number.isNaN(date.getTime()) ? "" : format(date, "MMM d");
};

interface WPRequestsListProps {
  workplaceRequests: WorkplaceRequestElement[];
  schedule: ISchedule;
  /** Room the requests are shown under; falls back to the reservation's own. */
  resource?: IResource;
  onRequestClick?: (request: WorkplaceRequestElement) => void;
}

/**
 * The workplace requests filed against a reservation, one card per request,
 * plus the entry point to file a new one against the same reservation.
 */
export const WPRequestsList = ({
  workplaceRequests,
  schedule,
  resource,
  onRequestClick
}: WPRequestsListProps) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const isAdmin = canScheduleOthers(authenticatedUser);
  const isMobile = useIsMobile();
  const { openModal } = useWorkplaceRequestModal();
  const typeVisuals = useWorkplaceRequestTypeVisuals();
  const invalidateRequests = useInvalidateWorkplaceRequestsByReservation();
  // The timeline cards carry a badge per request, from their own gantt cache,
  // so a request filed here has to refresh the grid behind this view too.
  const invalidateTimelineEvents = useInvalidateTimelineEvents();
  const [selectedRequest, setSelectedRequest] =
    useState<WorkplaceRequestElement | null>(null);
  const [isWizardOpen, setIsWizardOpen] = useState(false);

  // On mobile the details open in the shared modal; on desktop they open in the
  // panel below, which cannot be shown on a small screen.
  const openRequest = (request: WorkplaceRequestElement) => {
    if (onRequestClick) return onRequestClick(request);
    if (isMobile) return openModal(request);
    setSelectedRequest(request);
  };

  const room = resource ?? schedule.resource;
  // The API sends the office's wall-clock time behind a "Z".
  const scheduleStart = convertUTCToLocalDate(schedule.start);

  return (
    <div className="my-2 flex w-full flex-col gap-2">
      {workplaceRequests.map((request) => {
        const visual = typeVisuals.get(request.type.id);
        return (
          <button
            key={request.id}
            type="button"
            onClick={() => openRequest(request)}
            className="flex w-full items-center gap-3 rounded-lg border border-neutral-200 bg-white p-3 text-left transition-colors hover:bg-neutral-50"
          >
            <IconTile size="md" color={visual?.color} icon={visual?.icon} />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-inter font-medium text-neutral-1000 text-sm">
                {request.type.name}
              </span>
              <span className="block truncate text-neutral-700 text-xs">
                {request.title}
              </span>
              <span className="block truncate text-neutral-400 text-xs">
                {request.display_id} · Filed {formatFiledOn(request.created)} ·{" "}
                {request.submitter_user?.name}
              </span>
            </span>
            <RequestStatusPill
              color={getRequestStatusColor(request.status)}
              label={request.sub_status?.name ?? capitalize(request.status)}
            />
            <ChevronRight className="size-4 flex-shrink-0 text-neutral-400" />
          </button>
        );
      })}

      <Button
        type="button"
        variant="outline"
        size="sm"
        iconLeft={Plus}
        className="self-start"
        onClick={() => setIsWizardOpen(true)}
      >
        New Request
      </Button>

      {isWizardOpen && (
        <NewRequestWizard
          isOpen
          onOpenChange={(open) => {
            setIsWizardOpen(open);
            if (open) return;
            invalidateRequests(schedule.id);
            invalidateTimelineEvents();
          }}
          scheduleId={schedule.id}
          resource={
            room
              ? {
                  id: room.id,
                  name: room.name,
                  officeId: room.office_id,
                  type: room.type
                }
              : undefined
          }
          // The request is tied to this reservation's room — switching office
          // here would detach that location and could file the ticket under
          // an office that doesn't match the reservation.
          lockedOfficeId={room?.office_id}
          // A reservation question starts on this reservation's day and hours.
          reservationWhen={{
            days: [scheduleStart],
            start: scheduleStart,
            end: convertUTCToLocalDate(schedule.end)
          }}
        />
      )}

      {selectedRequest && (
        <RequestDetailsPage
          dontTouchUrl={true}
          requestId={selectedRequest.id}
          isAdmin={isAdmin}
          onRequestUpdate={(updatedRequest: WorkplaceRequestElement) => {
            setSelectedRequest(updatedRequest);
            // Status/title edits made from this panel bypass the wizard's own
            // close handler, so the request cards and timeline badges need
            // their own refresh here too.
            invalidateRequests(schedule.id);
            invalidateTimelineEvents();
          }}
          isPageMode={false}
          initialRequest={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          schedule={schedule}
        />
      )}
    </div>
  );
};
